import { EmbroideryPurpose, LogoFile } from '../../types'
import { filterEmbroideryVariant } from '../../useEmbroideryVariants'
import { EmbroideryItem } from '../types'

import type { EmbroideryProviderEmbroideryVariantFieldsFragment } from '../../../../__generated__/graphql/catalog/graphql'

/**
 * Mirrors the `embroideryVariant`/`embroiderySetupVariant`/`embroiderySetupVariantForBag` memos in
 * `EmbroideryProvider.tsx`, so both stacks add the same line items to cart;
 * `facadeParity.test.tsx` checks they stay in sync.
 *
 * @param items - Every selected item. Only the ones going in the bag decide what is bought.
 * @param embroideryVariants - The flow's priced variants, from the pricing provider.
 * @param digitizationVariant - The setup product. It carries no embroidery type tag, so it never
 *   appears among the flow's priced variants and has to be resolved by its key.
 * @param transientLogo - A logo already being digitized by a setup line in the cart, if any.
 */
export const bagVariantsFromItems = ({
  items,
  embroideryVariants,
  embroideryPurpose,
  digitizationVariant,
  transientLogo,
}: {
  items: readonly EmbroideryItem[]
  embroideryVariants: readonly EmbroideryProviderEmbroideryVariantFieldsFragment[]
  embroideryPurpose: EmbroideryPurpose
  digitizationVariant: EmbroideryProviderEmbroideryVariantFieldsFragment | undefined
  transientLogo: LogoFile | undefined
}): {
  embroideryItemVariant: EmbroideryProviderEmbroideryVariantFieldsFragment | undefined
  /** The setup product going in the bag: none while the logo is unchecked. */
  embroiderySetupVariant: EmbroideryProviderEmbroideryVariantFieldsFragment | undefined
} => {
  const bagItems = items.filter(item => item.enabledForBag)
  const logoItem = items.find(item => item.typeKey === 'logo')
  const isLogoEnabledForBag = Boolean(logoItem?.enabledForBag)

  const matches = embroideryVariants.filter(variant =>
    filterEmbroideryVariant(variant, {
      withText: bagItems.some(item => item.typeKey === 'text'),
      withIcon: bagItems.some(item => item.typeKey === 'icon'),
      withLogo: isLogoEnabledForBag,
      declineColor:
        isLogoEnabledForBag && logoItem?.values.logoFile?.digitizationSetting === 'BLACK_WHITE',
      forB2b: embroideryPurpose === 'B2B',
    })
  )

  // A logo that is already being digitized by a setup line in the cart must not be charged twice.
  const alreadyBeingDigitized =
    Boolean(transientLogo) && transientLogo?.id === logoItem?.values.logoFile?.id
  const needsSetup = logoItem?.values.requiresSetup && !alreadyBeingDigitized

  return {
    embroideryItemVariant: matches.length === 1 ? matches[0] : undefined,
    embroiderySetupVariant: isLogoEnabledForBag && needsSetup ? digitizationVariant : undefined,
  }
}
